import React, { useState } from 'react';
import { StatusBadge } from '../components/StatusBadge';
import { Search } from 'lucide-react';
import { ApplicationDetailModal } from '../components/ApplicationDetailModal';
function parseDisplayDate(dateStr: string): Date {
  const months: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11
  };
  const parts = dateStr.split(' ');
  const day = parseInt(parts[0], 10);
  const month = months[parts[1]];
  const year = parseInt(parts[2], 10);
  return new Date(year, month, day);
}
export function ApplicationsPage() {
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedApplication, setSelectedApplication] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Mock Data — with submission dates spread across multiple months
  const [applications, setApplications] = useState([{
    id: 'APP-55679',
    customerId: 'CUST-10234',
    applicant: 'City Builders',
    amount: 'PKR 2,500,000',
    tenure: '24 months',
    status: 'Active',
    charges: 'PKR 25,000',
    submissionDate: '28 Nov 2025'
  }, {
    id: 'APP-55680',
    customerId: 'CUST-10567',
    applicant: 'Tech Solutions',
    amount: 'PKR 500,000',
    tenure: '6 months',
    status: 'Settled',
    charges: 'PKR 5,000',
    submissionDate: '15 Dec 2025'
  }, {
    id: 'APP-55682',
    customerId: 'CUST-10892',
    applicant: 'Retail Mart',
    amount: 'PKR 300,000',
    tenure: '6 months',
    status: 'Active',
    charges: 'PKR 3,500',
    submissionDate: '20 Jan 2026'
  }]);
  const handleRowClick = (item: any) => {
    setSelectedApplication(item);
    setIsModalOpen(true);
  };
  const filteredData = applications.filter(item => {
    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
    const matchesSearch = item.id.toLowerCase().includes(searchTerm.toLowerCase()) || item.applicant.toLowerCase().includes(searchTerm.toLowerCase()) || item.customerId.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesDate = true;
    if (selectedDate) {
      const itemDate = parseDisplayDate(item.submissionDate);
      const filterDate = new Date(selectedDate + 'T00:00:00');
      matchesDate = itemDate.getFullYear() === filterDate.getFullYear() && itemDate.getMonth() === filterDate.getMonth() && itemDate.getDate() === filterDate.getDate();
    }
    return matchesStatus && matchesSearch && matchesDate;
  });
  return <div className="space-y-6" data-id="element-603">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-604">
        <div data-id="element-605">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-606">Applications</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-607">
            View loan applications across lifecycle stages.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-4 items-end" data-id="element-608">
        <div className="w-full sm:flex-1" data-id="element-609">
          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-610">
            Search Application ID, Customer ID, or Applicant
          </label>
          <div className="relative rounded-md shadow-sm" data-id="element-611">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3" data-id="element-612">
              <Search className="h-4 w-4 text-gray-400" aria-hidden="true" data-id="element-613" />
            </div>
            <input type="text" className="block w-full rounded-md border-gray-300 pl-10 focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-614" />
          </div>
        </div>

        <div className="w-full sm:w-48" data-id="element-615">
          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-616">
            Date
          </label>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border" data-id="element-617" />
        </div>

        <div className="w-full sm:w-64" data-id="element-618">
          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-619">
            Status
          </label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm py-2 px-3 border" data-id="element-620">
            <option value="All" data-id="element-621">All Applications</option>
            <option value="Active" data-id="element-622">Active</option>
            <option value="Settled" data-id="element-623">Settled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg overflow-hidden" data-id="element-624">
        <div className="overflow-x-auto" data-id="element-625">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-626">
            <thead className="bg-gray-50" data-id="element-627">
              <tr data-id="element-628">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-629">
                  Application ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-630">
                  Customer ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-631">
                  Applicant Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-632">
                  Loan Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-633">
                  Tenure
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-634">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-635">
                  Submission Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-636">
                  Charges
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-637">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-638">
              {filteredData.map(item => <tr key={item.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => handleRowClick(item)} data-id="element-639">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-640">
                    {item.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-641">
                    {item.customerId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-642">
                    {item.applicant}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-643">
                    {item.amount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-644">
                    {item.tenure}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-645">
                    <StatusBadge status={item.status} data-id="element-646" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-647">
                    {item.submissionDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-648">
                    {item.charges}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-649">
                    <div className="flex items-center justify-end gap-2" data-id="element-650">
                      <button onClick={e => {
                    e.stopPropagation();
                    handleRowClick(item);
                  }} className="text-blue-600 hover:text-blue-900" data-id="element-651">
                        View Details
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <ApplicationDetailModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={selectedApplication} data-id="element-652" />
    </div>;
}